import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { CaretUpAshIcon, CaretDownAshIcon } from '@glu/icons-react';
import locale from '@glu/locale';

const Link = (props) => {
  const {
    active,
    children,
    classes,
    CollapseIcon,
    dataQa,
    divider,
    ExpandIcon,
    hasIcon,
    isHeader,
    LinkComponent,
    navLinks,
    onClick,
    open,
    tier,
    toggleOpen,
    /* These shouldn't be passed in as a prop to the input. underscore indicates not used */
    alignRight: _alignRight, // eslint-disable-line react/prop-types
    shadeOnHover: _shadeOnHover, // eslint-disable-line react/prop-types
    relativePosition: _relativePosition, // eslint-disable-line react/prop-types
    menu: _menu, // eslint-disable-line react/prop-types
    ...rest
  } = props;

  if (isHeader) {
    return (
      <h3
        className={classNames(
          classes[`tier${tier}-header`],
          classes.header
        )}
        data-qa={dataQa}
        {...rest}
      >
        {children}
      </h3>
    );
  }

  const isAnchor = !navLinks;
  if (isAnchor) {
    return (
      <LinkComponent
        className={classNames(classes[`tier${tier}-link`], {
          [classes.rootActive]: active
        })}
        data-qa={dataQa}
        onClick={onClick}
        {...rest}
      >
        {children}
      </LinkComponent>
    );
  }

  return (
    <button
      data-qa={dataQa}
      type="button"
      onClick={toggleOpen}
      className={classNames(classes[`tier${tier}-link`], {
        [classes.linkActive]: open,
        [classes.rootActive]: active
      })}
    >
      {children}
      {navLinks.length && hasIcon && <ExpandIcon className={classes.icon} title={locale.get('nav.open')} />}
    </button>
  );
};

Link.propTypes = {
  classes: PropTypes.shape({
    header: PropTypes.string,
    linkActive: PropTypes.string,
    rootActive: PropTypes.string,
    icon: PropTypes.string
  }).isRequired,
  /* State of link. True if open or current */
  active: PropTypes.bool,
  /* Children of link component. Can take custom markup or component */
  children: PropTypes.node.isRequired,
  /** Icon indicating that the children nav items are open and can be closed */
  CollapseIcon: PropTypes.func,
  /** data attribute for easy targeting during qa testing */
  dataQa: PropTypes.string,
  /** toggle display of thin line to right of link */
  divider: PropTypes.bool,
  /** icon indicating children of this link can be expanded */
  ExpandIcon: PropTypes.func,
  /** Toggle display of down chevron for children */
  hasIcon: PropTypes.bool,
  /** Toggle if the link should display as a link or plain markup */
  isHeader: PropTypes.bool,
  /** url target for the link */
  href: PropTypes.string,
  /** custom component when a tag or button is not desired */
  LinkComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /** array of objects with nav link data */
  navLinks: PropTypes.arrayOf(PropTypes.shape({
    /** typically a string, but can also be provided as a component */
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    /** custom component to override the default sub menu component */
    menu: PropTypes.node,
    /** custom component rendered imediately after the children */
    footer: PropTypes.node,
    /** callback after clicked.  */
    onClick: PropTypes.func,
    /** used on top nav tabs. visual seperator of links. useful when links are images */
    divider: PropTypes.bool,
    /** display icon indicating child navLinks. required for setting styles correctly */
    hasIcon: PropTypes.bool,
    /** render children as plain markup instead of a button */
    isHeader: PropTypes.bool,
    /** toggle relative position of tabs so that sub menus can be styled */
    relativePosition: PropTypes.bool,
    /** toggle if tab receives background shading on hover.
     * typically used in combination with custom children components */
    shadeOnHover: PropTypes.bool,
    /** link href. not used if link is a parent of navLinks */
    href: PropTypes.string
  })),
  /** callback when link is clicked */
  onClick: PropTypes.func,
  /** changes display based on if children are visible or not */
  open: PropTypes.bool,
  /** level of nested link. useful for targeting child styles */
  tier: PropTypes.number,
  /** function for opening and closing child links  */
  toggleOpen: PropTypes.func
};

Link.defaultProps = {
  active: undefined,
  CollapseIcon: CaretUpAshIcon,
  dataQa: undefined,
  divider: false,
  ExpandIcon: CaretDownAshIcon,
  hasIcon: false,
  href: null,
  isHeader: false,
  LinkComponent: 'a',
  navLinks: null,
  onClick: null,
  open: false,
  tier: 0,
  toggleOpen: null
};

export default Link;
